import React, { useMemo } from 'react';
import { useQuery } from 'react-query';
import { getEventRanking } from "data/Querys";
import { ColumnRankNoResult, ColumnName, ColumnNoc, ColumnExpander } from "components/common/TableColumns"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faStop,
} from "@fortawesome/free-solid-svg-icons";
import Label from 'components/general/Label';
import Loader from 'components/general/Loader';
import MirsTable from 'components/general/table/Table.js';
import NotAvailable from 'components/common/NotAvailable';
import LoaderSubComponent from 'components/general/LoaderSubComponent';
import { NocMemo as Noc } from 'components/presentation/Noc';
import Competitor from 'components/presentation/Competitor';
import SportCode from "components/common/SportCode";
import PageHeader from 'components/common/PageHeader';
import { findArray } from 'helpers/utils';
import { useUrlFriendlyToParameters } from "helpers/urlFriendly";
import { usePositions, useTeamAthletesOrderByPositions } from "components/common/Positions";
import Stack from 'react-bootstrap/Stack';


function EventRanking() {
  const unitDescriptionForFinal = ['FEN', 'CLB']
  const unitDescription = ['CLB']
  const { mirs_discipline, mirs_rsc, mirs_competition, mirs_language } = useUrlFriendlyToParameters()

  //datos para el marcador y estadisticas
  const { data, isError, isLoading } = useQuery(
    {
      queryKey: ['eventRanking', mirs_competition, mirs_rsc, mirs_language],
      queryFn: getEventRanking,
      refetchInterval: 20000,
      staleTime: 20000,
      refetchIntervalInBackground: true,
      notifyOnChangeProps: ['data', 'error'],
      //enabled: !!confCategory
      onError: (error) =>
        console.error(`Something went wrong: ${error.message}`),
    });

  const tableColumnsDetail = (disc, street) => {
    return (
      [
        {
          id: 'Phase',
          Header: <Label data={'HeaderPhase'} />,
          headerClassName: "text-start w-30",
          className: 'text-start',
          accessor: (row) => (row?.phaseOrder),
          Cell: (props) => {
            let row = props?.cell?.row?.original
            const arrDisciplineUnit = ['CSP', 'ROW']
            return (
              <>{arrDisciplineUnit.includes(disc) ? row?.unit : row?.phase}</>
            )
          }
        },
        {
          id: 'Noc',
          Header: <Label data={'HeaderVS'} />,
          headerClassName: "text-start",
          className: 'text-start',
          Cell: (props) => {
            let rivalName = props.cell.row.original?.rival
              ? <Competitor hidePicture={true} {...props.cell.row.original?.rival} />
              : <SportCode discipline={mirs_discipline} entity='@CompetitorPlace' code={props?.cell?.row?.original?.competitorPlace} />
            return (
              <>
                <div className='d-flex align-items-center'>
                  <div className='event-ranking-noc d-none d-md-block'>
                    <Noc {...props?.cell?.row?.original?.rivalNoc} />
                  </div>

                  {rivalName}
                </div>
              </>
            )
          }
        },
        {
          id: 'NocArc',
          Header: <Label data={'HeaderVS'} />,
          headerClassName: "text-start",
          className: 'text-start',
          Cell: (props) => {
            let rivalName;
            let rivalNoc;
            if (props?.cell?.row?.original?.eventUnitCode.substring(22, 26) !== 'QUAL') {
              rivalName = props.cell.row.original?.rival
                ? <Competitor hidePicture={true} {...props.cell.row.original?.rival} />
                : <SportCode discipline={mirs_discipline} entity='@CompetitorPlace' code={props?.cell?.row?.original?.competitorPlace} />
              rivalNoc = <Noc {...props?.cell?.row?.original?.rivalNoc} />
            }
            return (
              <>
                <div className='d-flex align-items-center'>
                  <div className='event-ranking-noc d-none d-md-block'>
                    {rivalNoc}
                  </div>
                  {rivalName}
                </div>
              </>
            )
          }
        },
        {
          id: 'Pos',
          Header: <Label data={'HeaderPlace'} />,
          headerClassName: "text-start",
          className: 'text-start',
          Cell: (props) => {
            return (
              <>
                {props?.cell?.row?.original?.teamPos}
              </>
            )
          }
        },
        {
          id: 'Rank',
          Header: <Label data={'HeaderRank'} />,
          headerClassName: '|SKB|ROW|CSP|BMX'.includes(disc)
            ? 'text-start w-10'
            : 'text-end',
          className: '|SKB|ROW|CSP|BMX'.includes(disc)
            ? 'text-start w-10'
            : 'text-end',
          Cell: (props) => {
            return (
              <>
                {props?.cell?.row?.original?.teamRank}
              </>
            )
          }
        },
        {
          id: 'Run1',
          Header: <Label data={'HeaderRun1'} sport={'BMF'} />,
          headerClassName: "text-end",
          className: 'text-end',
          Cell: (props) => {
            return (
              <>
                {props?.cell?.row?.original?.run1}
              </>
            )
          }
        },
        {
          id: 'Run2',
          Header: <Label data={'HeaderRun2'} sport={'BMF'} />,
          headerClassName: "text-end",
          className: 'text-end',
          Cell: (props) => {
            return (
              <>
                {props?.cell?.row?.original?.run2}
              </>
            )
          }
        },
        {
          id: 'WLT',
          className: 'text-end w-5',
          Cell: (props) => {
            let wlt = props?.cell?.row?.original?.teamWLT?.trim() === 'W' ? <FontAwesomeIcon className='' icon={faCheck} /> : ''
            return (
              <>
                {wlt}
              </>
            )
          }
        },
        {
          id: 'WLTRank',
          className: 'text-end w-5',
          Cell: (props) => {
            const row = props?.cell?.row?.original;
            let wlt = row?.teamWLT?.trim() === 'W' ? <FontAwesomeIcon className='' icon={faCheck} /> : row?.teamRank;
            return (<>{wlt}</>);
          }
        },
        {
          id: 'Result',
          Header: '|DIV|GTR|MPN|'.includes(disc) || /CLB.BLCOMB-.+/.test(mirs_rsc)
            ? <Label data={'HeaderPoints'} />
            : '|CSL|CLB|ROW|CSP|SWM|BMX'.includes(disc)
              ? <Label data={'HeaderTime'} />
              : disc === 'BMF'
                ? <Label data={'HeaderBestAvg'} sport={'BMF'} />
                : disc === 'SKB'
                  ? <Label data={street ? 'HeaderTotal' : 'HeaderScore'} />
                  : '|GRY|KTE'.includes(disc)
                    ? <Label data={'HeaderScore'} />
                    : <Label data={'HeaderResult'} />,
          headerClassName: '|CLB|GRY|MPN|'.includes(disc)
            ? 'text-end w-15'
            : 'text-start w-15',
          className: '|BMF|ROW|CSP|BMX'.includes(disc)
            ? 'w-15'
            : disc === 'SKB'
              ? 'text-start'
              : 'text-end w-15',
          Cell: (props) => {
            return (
              <>
                {props?.cell?.row?.original?.result}
              </>
            )
          }
        }
      ])
  }

  const tableColumns = useMemo(() => {
    let arrTypeDiscipline = ['FBB', 'FBL', 'VBV', 'GTR']
    let labelName = mirs_discipline === 'ROW' ? 'HeaderCrew' : undefined
    let type = arrTypeDiscipline.includes(mirs_discipline) ? 'alwaysTrue2' : 'eventRanking'
    return (
      [
        ColumnRankNoResult({ disableSort: true, isEventRanking: true }),
        ColumnNoc({ disableSort: true }),
        ColumnName({ disableSort: true, label: labelName }),
        ColumnExpander({ type: type })
      ]
    )
  }
  )

  // Create a function that will render our row sub components
  function RenderPhases({ event_code, team_code, athlete_code, type }) {
    const code = type === 'A' ? athlete_code : team_code;
    let street = ''
    if (event_code.includes('STREET')) {
      street = true
    }
    let rounds = [];
    const hideTableDbq = code && '|GRY|'.includes(mirs_discipline) && data?.event?.rankings?.find(rk => rk?.athlete_code === code || rk?.team_code === code)?.rk_IRM === 'DQB'
    if ('|MPN|'.includes(mirs_discipline)) {
      let rankingResult = data?.event?.rankings?.find(a => a.athlete_code === code)
      let hasFnlResults = rankingResult?.rk_Result
      code && data?.event?.phases
        ?.filter(phase_ => phase_?.type === '3' && ((phase_?.order === '1' && hasFnlResults) || phase_?.order === '2'))
        ?.sort((a, b) => Number(a.order) - Number(b.order))
        ?.map(phase => {
          let textPhase = phase?.shortDescription
          let result
          result = <ResultMpnRace rankingResult={rankingResult} order={phase?.order} />
          rounds.push({
            phase: textPhase,
            phaseOrder: Number(phase?.order),
            teamRank: phase?.order === '1'
              ? rankingResult?.rank
              : findArray(rankingResult?.extendedResults?.find(a => a.extensions)?.extensions, { ere_code: ['RANK'] })?.ere_value,
            result: result
          })
        })
    }
    else if ('|GRY|'.includes(mirs_discipline) || /CLB.BLCOMB-.+/.test(mirs_rsc)) {
      code && data?.event?.phases
        ?.filter(phase_ => phase_?.type === '3')
        ?.sort((a, b) => Number(a.order) - Number(b.order))
        ?.map(phase => {
          let rival = phase?.cumulative?.results?.find(res => res?.participant?.code !== code)
          return phase?.cumulative?.results?.filter(res => res?.participant?.code === code)?.map(item => {
            let textPhase = phase?.shortDescription
            let result
            if (mirs_discipline === 'GRY') {
              result = <ResultDefaultRace unit={phase?.cumulative} code={code} isCumulativeSource={true} />
            } else {
              result = <ResultDefaultRace unit={phase?.cumulative} code={code} isCumulativeSource={true} />
            }
            rounds.push({
              phase: textPhase,
              phaseOrder: Number(phase?.order),
              eventUnitCode: phase?.cumulative?.documentCode,
              dateMatch: '',
              teamRes: item?.resultData,
              teamWLT: item?.resultWLT,
              teamPos: item?.sortOrder,
              teamRank: item?.resultRank,
              //Team athletes from DT_RANKING
              teamAthletes: findArray(data?.event?.rankings, { team_code: [item?.participant?.code] }).teamAthletes,
              //Team athletes from DT_RESULT
              teamAthletesResult: item?.teamAthletes,
              run1: '',
              run2: '',
              rival: rival?.participant,
              rivalNoc: rival?.participant?.organisation,
              rivalRes: rival?.resultData,
              rivalCode: `${rival?.participant?.code}_${phase?.cumulative?.documentCode}`,
              competitorPlace: rival?.teamCode ? rival?.teamCode : rival?.athleteCode,
              result: result
            })
          })


        })

    } else {
      code && data?.event?.phases
        ?.filter(phaseFilter => phaseFilter?.type === "3")
        ?.sort((a, b) => Number(a.order) - Number(b.order))
        ?.map(phase =>
          phase?.units
            ?.sort((a, b) => new Date(b?.result?.date) - new Date(a?.result?.date))
            ?.map(unit => {

              let rival = unit?.result?.items?.find(it => it?.participant?.code !== code)
              return unit?.result?.items?.map(item => {

                let textPhase = phase?.shortDescription
                if (unitDescriptionForFinal?.includes(mirs_discipline) && phase?.order?.trim() === '1') {
                  textPhase = `${textPhase} - ${unit?.shortDescription}`
                }
                if (unitDescription?.includes(mirs_discipline)) {
                  textPhase = unit?.shortDescription
                }
                if (item?.participant?.code === code) {
                  let result;
                  if (mirs_discipline === 'ARC') {
                    result = <ResultArc unit={unit} code={code} />
                  } else if (mirs_discipline === 'BDM') {
                    result = <ResultBdm unit={unit} code={code} />
                  } else if (mirs_discipline === 'BMF') {
                    result = <ResultBmf unit={unit} code={code} />
                  } else if (mirs_discipline === 'BMX') {
                    result = <ResultBmx unit={unit} code={code} />
                  } else if (mirs_discipline === 'DIV') {
                    result = <ResultDiv unit={unit} code={code} />
                  } else if (mirs_discipline === 'FBL' || mirs_discipline === 'FBB') {
                    result = <ResultDefaultH2hIrmPSO unit={unit} code={code} />
                  } else if (mirs_discipline === 'GBL') {
                    result = <ResultDefaultH2hIrmPSO unit={unit} code={code} />
                  } else if (['FEN', 'WFE'].includes(mirs_discipline)) {
                    result = <ResultFen unit={unit} code={code} />
                  } else if (mirs_discipline === 'HOC') {
                    result = <ResultHoc unit={unit} code={code} />
                  } else if (mirs_discipline === 'JUD') {
                    result = <ResultJud unit={unit} code={code} />
                  } else if (mirs_discipline === 'SRF') {
                    result = <ResultSrf unit={unit} code={code} />
                  } else if (mirs_discipline === 'PDL') {
                    result = <ResultPdl unit={unit} code={code} />
                  } else if (mirs_discipline === 'BKG') {
                    result = <ResultBkg unit={unit} code={code} />
                  } else if (['BOX', 'KIK', 'MTI'].includes(mirs_discipline)) {
                    result = <ResultDefaultCombat unit={unit} code={code} />
                  } else if (mirs_discipline === 'WRE') {
                    result = <ResultWre unit={unit} code={code} />
                  } else if (mirs_discipline === 'SWM') {
                    result = <ResultSwm unit={unit} code={code} />
                  } else if (mirs_discipline === 'SKB') {
                    result = <ResultSkb unit={unit} code={code} />
                  } else if (['VBV', 'VVO', 'VBS', 'KTE'].includes(mirs_discipline)) {
                    result = <ResultDefaultH2hIrm unit={unit} code={code} />
                  } else if (['CLB', 'CSL', 'CSP', 'GRY', 'ROW', 'SHO', 'GTR'].includes(mirs_discipline)) {
                    result = <ResultDefaultRace unit={unit} code={code} />
                  } else {
                    result = <ResultDefaultH2H unit={unit} code={code} />
                  }

                  rounds.push({
                    unit: unit?.shortDescription,
                    phase: textPhase,
                    phaseOrder: Number(phase?.order),
                    eventUnitCode: unit?.result?.eventUnitCode,
                    dateMatch: unit?.result?.date,

                    teamRes: item?.resultData,
                    teamWLT: item?.resultWLT,
                    teamPos: item?.sortOrder,
                    teamRank: item?.resultRank,
                    //Team athletes from DT_RANKING
                    teamAthletes: findArray(data?.event?.rankings, { team_code: [item?.participant?.code] }).teamAthletes,
                    //Team athletes from DT_RESULT
                    teamAthletesResult: item?.teamAthletes,

                    run1: findArray(item?.extendedResults, { er_pos: ['RUN1'] })?.er_irm
                      ? findArray(item?.extendedResults, { er_pos: ['RUN1'] })?.er_irm
                      : findArray(item?.extendedResults, { er_pos: ['RUN1'] })?.er_value,

                    run2: findArray(item?.extendedResults, { er_pos: ['RUN2'] })?.er_irm
                      ? findArray(item?.extendedResults, { er_pos: ['RUN2'] })?.er_irm
                      : findArray(item?.extendedResults, { er_pos: ['RUN2'] })?.er_value,

                    rival: rival?.participant,
                    rivalNoc: rival?.participant?.organisation,
                    rivalRes: rival?.resultData,
                    rivalCode: `${rival?.participant?.code}_${unit?.result?.eventUnitCode}`,
                    competitorPlace: rival?.teamCode ? rival?.teamCode : rival?.athleteCode,
                    result: result
                  })
                }

              })
            }
            )
        )

    }

    let hiddenColumns = []
    switch (mirs_discipline) {
      case 'ARC':
        hiddenColumns = ['Pos', 'Rank', 'Run1', 'Run2', 'Noc', 'WLTRank']
        break;
      case 'BMF':
        hiddenColumns = ['Pos', 'NocArc', 'Rank', 'Noc', 'WLTRank']
        break;
      case 'BMX':
        hiddenColumns = ['Pos', 'NocArc', 'Run1', 'Run2', 'Noc', 'WLT', 'WLTRank']
        break;
      case 'CLB':
        hiddenColumns = ['Pos', 'Rank', 'Run1', 'Run2', 'NocArc', 'WLT']
        break;
      case 'CSL':
        hiddenColumns = ['Pos', 'NocArc', 'Run1', 'Run2', 'Noc', 'WLTRank']
        break;
      case 'CSP':
        hiddenColumns = ['Pos', 'NocArc', 'Run1', 'Run2', 'Noc', 'WLTRank']
        break;
      case 'DIV':
        hiddenColumns = ['Pos', 'Noc', 'Run1', 'Run2', 'NocArc', 'WLTRank']
        break;
      case 'GRY':
        hiddenColumns = ['Pos', 'Noc', 'Run1', 'Run2', 'NocArc', 'WLTRank']
        break;
      case 'GTR':
        hiddenColumns = ['Pos', 'NocArc', 'Run1', 'Run2', 'Noc', 'WLTRank']
        break;
      case 'MPN':
        hiddenColumns = ['Pos', 'NocArc', 'Run1', 'Run2', 'Noc', 'WLTRank']
        break;
      case 'ROW':
        hiddenColumns = ['Pos', 'Noc', 'Run1', 'Run2', 'NocArc', 'WLTRank']
        break;
      case 'SHO':
        hiddenColumns = ['Pos', 'NocArc', 'Run1', 'Run2', 'Noc', 'WLTRank']
        break;
      case 'SRF':
        hiddenColumns = ['Noc', 'WLT', 'Rank', 'Run1', 'Run2', 'NocArc', 'WLTRank']
        break;
      case 'SWM':
        hiddenColumns = ['Pos', 'NocArc', 'Run1', 'Run2', 'Noc', 'WLTRank']
        break;
      case 'SKB':
        hiddenColumns = ['Pos', 'NocArc', 'Run1', 'Run2', 'Noc', 'WLT', 'WLTRank']
        break;
      default:
        hiddenColumns = ['Pos', 'Rank', 'Run1', 'Run2', 'NocArc', 'WLTRank']
        break;
    }

    if (/CLB.BLCOMB-.+/.test(mirs_rsc)) {
      hiddenColumns = ['Pos', 'Run1', 'Run2', 'NocArc', 'Noc', 'WLT', 'WLTRank']
    }

    const positions = usePositions();
    const athletesSorts = useTeamAthletesOrderByPositions(rounds[0]?.teamAthletesResult, true);

    return (
      <>
        {mirs_discipline === 'SWM' && mirs_rsc.includes('SWMX')
          ? <div className='competitor-box'>{rounds[0]?.teamAthletes?.map((ath, index) =>
            <div className='competitor-gender' key={index}>
              <Competitor {...ath.participant} hidePicture={true} hideNoc={true} />
              &nbsp;{ath?.participant?.personGender?.code}
            </div>
          )}</div>
          : <></>
        }

        {mirs_discipline === 'ROW' && type !== 'A'
          ? <div className='competitor-box'>{athletesSorts?.map((ath, index) =>
            <div className='competitor-list' key={index}>
              <Competitor {...ath.athlete} hidePicture={true} hideNoc={true} />
              &nbsp;-&nbsp;{positions(ath?.eventUnitEntries.find(item => item.code = 'POSITION')?.eue_value)}
            </div>
          )}</div>
          : <></>
        }

        {mirs_discipline === 'CSP' && type !== 'A'
          ? <div className='competitor-box'>{rounds[0]?.teamAthletesResult?.map((ath, index) =>
            <div className='competitor-list' key={index}>
              <div className='text-center w-20'>
                <Competitor {...ath.athlete} hidePicture={true} hideNoc={true} />
              </div>
            </div>
          )}</div>
          : <></>
        }
        {/GRY.5AA-.+/.test(mirs_rsc)
          ? <div className='competitor-box'>{rounds[0]?.teamAthletesResult?.map(ath =>
            <div className='competitor-list' key={ath?.participantCode?.code}>
              <div className='text-center w-20'>
                <Competitor {...ath.participantCode} hidePicture={true} hideNoc={true} />
              </div>
            </div>
          )}</div>
          : <></>
        }

        <MirsTable
          className={hideTableDbq ? 'd-none' : 'table-details'}
          data={rounds}
          columns={useMemo(() => tableColumnsDetail(mirs_discipline, street))}
          hiddenColumns={hiddenColumns}
          keyRow={"rivalCode"}
          //initialSortBy={[{ id: 'Phase', desc: false }]}
          id={`mirs-table-event-ranking-detail-${mirs_rsc}`}
        />
      </>
    )
  }
  const renderRowSubComponent = React.useCallback(({ row }) => (
    <LoaderSubComponent component={<RenderPhases
      event_code={row?.original?.event_code}
      team_code={row?.original?.team_code}
      athlete_code={row?.original?.athlete_code}
      type={row?.original?.rk_Type}
    />}
    />
  ), [data]);

  if (isError) { return <div >No data available</div>; }
  if (isLoading) { return <Loader />; }

  let keyRow = "team_code"
  if (data?.event?.rankings !== undefined && data?.event?.rankings[0] !== undefined) {
    keyRow = data?.event?.rankings[0]?.rk_Type === 'A' ? "athlete_code" : keyRow;
  }

  return (
    <>
      <PageHeader title={<Label data="LabelIndividualMedalsRanking" />} showEvent={true} />

      <div className='mirs-page-container'>
        {data?.event?.rankings ? <MirsTable
          className='table-result'
          data={data?.event?.rankings}
          columns={tableColumns}
          keyRow={keyRow}
          colIndex={1}
          caption={'Event Ranking'}
          initialSortBy={[{ id: 'RankNoResult', desc: false }]}
          id={`mirs-table-event-ranking-${mirs_rsc}`}
          renderRowSubComponent={renderRowSubComponent}
        /> : <NotAvailable />}
      </div>
    </>

  )
}

export default EventRanking

const ResultDefaultH2H = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()
  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)
  return (
    <Stack direction='horizontal' className='justify-content-center'>
      <div className='w-40'>{main?.resultData}</div>
      <div className='text-center'>{rival?.resultData && ' - '}</div>
      <div className='w-40 text-start'>{rival?.resultData}</div>
    </Stack>
  )
}

const ResultDefaultH2hIrm = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()
  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)
  const irmMain = main?.resultIrm ? <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} /> : <></>
  const irmRival = rival?.resultIrm ? <SportCode discipline={mirs_discipline} entity='@IRM' code={rival?.resultIrm} abbr={true} /> : <></>
  return (
    <Stack direction='horizontal' className='justify-content-center'>
      <div className='w-40'>{irmMain} {main?.resultData}</div>
      <div className='text-center'>{rival?.resultData && ' - '}</div>
      <div className='w-40 text-start'>{rival?.resultData} {irmRival}</div>
    </Stack>
  )
}

const ResultDefaultH2hIrmPSO = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()
  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)
  const irmMain = main?.resultIrm ? <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} /> : <></>
  const irmRival = rival?.resultIrm ? <SportCode discipline={mirs_discipline} entity='@IRM' code={rival?.resultIrm} abbr={true} /> : <></>
  const pso = unit?.result?.periods?.find(it => it?.period_code.includes('-PSO'))
  const mainPSO = main?.startSortOrder === 1 ? pso?.home?.periodScore : pso?.away?.periodScore
  const rivalPSO = main?.startSortOrder === 1 ? pso?.away?.periodScore : pso?.home?.periodScore
  const resultPSO = pso && '(' + mainPSO + '-' + rivalPSO + ' '
  return (
    <>
      <Stack direction='horizontal' className='justify-content-center mx-0'>
        <div className='w-40'>{irmMain} {main?.resultData}</div>
        <div className='text-center'>{rival?.resultData && ' - '}</div>
        <div className='w-40 text-start'>{rival?.resultData} {irmRival}
          <span className='d-none d-md-inline'>{resultPSO && resultPSO} {resultPSO && <SportCode discipline={mirs_discipline} entity='@Period' code='PSO' abbr={true} />}{resultPSO && ')'}</span>
        </div>
      </Stack>
      <div className='text-center d-md-none d-block'>
        {resultPSO && resultPSO} {resultPSO && <SportCode discipline={mirs_discipline} entity='@Period' code='PSO' abbr={true} />}{resultPSO && ')'}
      </div>
    </>
  )
}
const ResultDefaultRace = ({ unit, code, isCumulativeSource }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()
  const main = isCumulativeSource
    ? unit?.results?.find(it => it?.participant?.code === code)
    : unit?.result?.items?.find(it => it?.participant?.code === code);
  const irm = main?.resultIrm ? <SportCode discipline={mirs_discipline} entity='@IRM' code={main?.resultIrm} abbr={true} /> : <></>

  return (
    <>
      {main?.resultData || ""}{irm}
    </>
  )
}

const ResultMpnRace = ({ rankingResult, order }) => {
  let result = ''
  if (order === '1') {
    result = rankingResult.rk_Result
  } else if (order === '2') {
    result = findArray(rankingResult?.extendedResults, { er_code: ['SEMIFINAL'] }).er_value
  }

  if (rankingResult?.resultType === 'IRM')
    result = rankingResult?.resultIrm

  return (
    <>
      {result}
    </>
  )
}


const ResultArc = ({ unit, code }) => {
  const main = unit?.result?.items?.find(it => it?.participant?.code === code);
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code);
  let result;
  if (unit?.result?.eventUnitCode.substring(22, 26) === 'QUAL') {
    result = main?.resultData ? main?.resultData + ' (' + main?.resultRank + ')' : '';
  } else {
    result = main?.resultData && rival?.resultData ? `${main?.resultData} - ${rival?.resultData} ` : main?.resultData ? main.resultData : '';
  }
  return (
    <>
      {result}
    </>
  )
}

const ResultBdm = ({ unit, code }) => {
  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)
  const games = unit?.result?.periods?.map(item => item?.home?.periodScore + '-' + item?.away?.periodScore).join(' ')
  let resData = ''
  if (main?.resultData) {
    resData = main?.resultData
  }
  let irm = ''
  if (rival?.resultIrm) {
    irm = rival?.resultIrm
  }
  resData = resData + irm
  return (
    <>
      {main?.resultData && rival?.resultData
        ? <div className='d-flex'>
          <div className='w-20 text-end'>{main?.resultData} - {rival?.resultData}</div>
          <div className='ps-2 w-60 text-start event-ranking-games align-self-center'>({games})</div>
          <div className='ps-2 w-20 text-start'>{irm}</div>
        </div>
        : <>{resData}</>
      }
    </>
  )
}

const ResultDiv = ({ unit, code }) => {
  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  return (
    <>
      {` ${main?.resultIrm ? main?.resultIrm : ''} ${main?.resultData ? main?.resultData : ''} `}
    </>
  )
}

const ResultFen = ({ unit, code }) => {
  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)
  const { mirs_discipline } = useUrlFriendlyToParameters()

  const winnerId = unit?.result?.extendedInfos?.find(ei => ei?.ei_code === 'WINNER_ID')?.ei_value
  const toss = unit?.result?.extendedInfos?.find(ei => ei?.ei_code === 'TOSS')?.ei_value

  let result = ''
  let isToss = false;
  let isTossRival = false;
  if (toss !== undefined) {
    isTossRival = winnerId === rival?.participant?.code;
    isToss = !isTossRival
  }
  result = `${isToss ? 'V(T)' : ''} ${main?.resultIrm ? main?.resultIrm : ''} ${main?.resultData ? main?.resultData : ''} - ${rival?.resultData ? rival?.resultData : ''} ${rival?.resultIrm ? rival?.resultIrm : ''} ${isTossRival ? 'V(T)' : ''} `
  if (rival?.participant?.organisation?.code === undefined) {
    result = <SportCode discipline={mirs_discipline} entity='@CompetitorPlace' code={rival?.teamCode ? rival?.teamCode : rival?.athleteCode} />
  }
  return (
    <>
      {result}
    </>
  )
}

const ResultHoc = ({ unit, code }) => {
  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)

  const pso = unit?.result?.periods?.find(p => p.period_code === `${unit?.result?.eventUnitCode} PSO`)
  const isMainHome = unit?.result?.items?.find(i => i.startSortOrder === 1)?.participant?.code === code
  let resultPso = pso && pso?.away && pso?.home ? isMainHome ? `(${pso?.home?.periodScore} - ${pso?.away?.periodScore})` : `(${pso?.away?.periodScore} - ${pso?.home?.periodScore})` : ''

  return (
    <>
      {main?.resultData && rival?.resultData ? `${main.resultData} - ${rival.resultData} ${resultPso} ` : ''}
    </>
  )
}


const ResultJud = ({ unit, code }) => {
  const { mirs_discipline } = useUrlFriendlyToParameters()
  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  let teamS = parseInt(main?.extendedResults?.find(filter => (filter.er_pos === 'TOT' && filter.er_code === 'S'))?.er_value)
  let teamH = main?.extendedResults?.find(filter => (filter.er_pos === 'TOT' && filter.er_code === 'H'))?.er_value
  let teamX = main?.extendedResults?.find(filter => (filter.er_pos === 'TOT' && filter.er_code === 'X'))?.er_value
  let teamS3 = main?.extendedResults?.find(filter => (filter.er_pos === 'TOT' && filter.er_code === 'S3'))?.er_value

  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)
  let rivalS = parseInt(rival?.extendedResults?.find(filter => (filter.er_pos === 'TOT' && filter.er_code === 'S'))?.er_value)
  let rivalH = rival?.extendedResults?.find(filter => (filter.er_pos === 'TOT' && filter.er_code === 'H'))?.er_value
  let rivalX = rival?.extendedResults?.find(filter => (filter.er_pos === 'TOT' && filter.er_code === 'x'))?.er_value
  let rivalS3 = rival?.extendedResults?.find(filter => (filter.er_pos === 'TOT' && filter.er_code === 'S3'))?.er_value

  let teamPenalties;
  let rivalPenalties;
  if (teamS > 0) {
    for (let i = 0; i < teamS; i++) {
      teamPenalties = <>{teamPenalties} <FontAwesomeIcon className='icon-yellow' icon={faStop} /></>
    }
  } else if (teamH === '1' || teamX === '1' || teamS3 === '1') {
    teamPenalties = <FontAwesomeIcon className='icon-red' icon={faStop} />
  }
  if (rivalS > 0) {
    for (let i = 0; i < rivalS; i++) {
      rivalPenalties = <>{rivalPenalties} <FontAwesomeIcon className='icon-yellow' icon={faStop} /></>
    }
  } else if (rivalH === '1' || rivalX === '1' || rivalS3 === '1') {
    rivalPenalties = <FontAwesomeIcon className='icon-red' icon={faStop} />
  }

  let result = (main?.resultData) ?
    (<div className='d-flex w-100 justify-content-center'>
      <div className='w-50'>{main?.resultData}{teamPenalties}</div>
      <div>&nbsp;-&nbsp;</div>
      <div className='w-50 text-start'>{rival?.resultData}{rivalPenalties}</div>
    </div>)
    :
    (<div className="w-100 text-center">
      <SportCode discipline={mirs_discipline} entity='@CompetitorPlace' code={rival?.teamCode ? rival?.teamCode : rival?.athleteCode} />
    </div>)
  return (
    <>
      {result}
    </>
  )
}

const ResultSrf = ({ unit, code }) => {
  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)
  return (
    <>
      {rival?.resultData !== undefined ? main?.resultData : ""}
    </>
  )
}

const ResultBkg = ({ unit, code }) => {
  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)
  const main_votes = main?.extendedResults?.find(filter => filter.er_code === 'VOTES')?.er_value
  const rival_votes = rival?.extendedResults?.find(filter => filter.er_code === 'VOTES')?.er_value
  return (
    <>
      {main?.resultData && rival?.resultData ? `${main?.resultData}-${rival?.resultData} (${main_votes}-${rival_votes})` : main?.resultData ? main.resultData : ''}
    </>
  )
}

const ResultWre = ({ unit, code }) => {
  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)
  const periods = unit?.result?.periods
  if (periods) {
    main.resultData = periods[1] ? periods[1]?.home?.score : periods[0]?.home?.score
    rival.resultData = periods[1] ? periods[1]?.away?.score : periods[0]?.away?.score
  }
  let winCon = findArray(unit?.result?.extendedInfos, { ei_code: ['RES_CODE'] }).ei_value ? findArray(unit?.result?.extendedInfos, { ei_code: ['RES_CODE'] }).ei_value : ''
  return (
    <div className='w-15'>
      {main?.resultData && rival?.resultData ? `${main?.resultData} - ${rival?.resultData} ${winCon}` : main?.resultData ? main.resultData : ''}
    </div>
  )
}

const ResultDefaultCombat = ({ unit, code }) => {
  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  const rival = unit?.result?.items?.find(it => it?.participant?.code !== code)
  let resCode = unit?.result?.extendedInfos?.find(ei => ei?.ei_code === 'RES_CODE')?.ei_value
  let resultType = main?.resultType
  let win = false
  if (main?.resultWLT?.trim() === 'W') {
    win = true
  }

  return (
    <>
      {win && resultType === 'POINTS' && main?.resultData && rival?.resultData
        ? `${resCode} ${main?.resultData}-${rival?.resultData}`
        : win && resultType === 'RM'
          ? `${main?.resultType}`
          : !win && resultType === 'POINTS' && main?.resultData && rival?.resultData
            ? `${main?.resultData}-${rival?.resultData}`
            : !win && resultType === 'RM'
              ? `${rival?.resultType}`
              : ''
      }
    </>
  )
}

const ResultSwm = ({ unit, code }) => {
  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  return (
    <>
      {main?.resultData ? `${main?.resultData}` : ''}
    </>
  )
}

const ResultBmf = ({ unit, code }) => {
  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  return (
    <>
      {main?.resultData ? `${main?.resultData}` : ''}
    </>
  )
}

const ResultBmx = ({ unit, code }) => {
  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  return (
    <>
      {main?.resultData ? `${main?.resultData}` : ''}
    </>
  )
}

const ResultSkb = ({ unit, code }) => {
  const main = unit?.result?.items?.find(it => it?.participant?.code === code)
  return (
    <>
      {main?.resultData ? `${main?.resultData}` : ''}
    </>
  )
}

const ResultPdl = ({ unit, code }) => {
  let periodsNum = unit?.result?.periods?.length

  const mainIrm = unit?.result?.items?.find(it => it?.participant?.code === code).resultIrm
  const rivalIrm = unit?.result?.items?.find(it => it?.participant?.code !== code).resultIrm

  if (mainIrm || rivalIrm) {
    return mainIrm ? mainIrm : rivalIrm ? rivalIrm : ''
  }

  return (
    <>
      {unit?.result?.periods
        ? unit?.result?.periods.map((p, index) =>
          <span key={index}>{p.home.score}-{p.away.score}{index !== periodsNum - 1 ? <>, </> : ''}</span>
        )
        : ''
      }
    </>
  )
}

